import { isLanguage, type Language } from '@kk/domain';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';

import { useClients, useSession } from '@/auth/SessionProvider';
import { portalLanguage } from './activeLanguage';
import { setPortalLanguage } from './applyLanguage';
import { deck } from './deck';

/** Where the browser remembers the switch (per browser; every read and write can throw in a locked-down browser). */
export const LANGUAGE_STORAGE_KEY = 'kk.portal.language';

export function readStoredLanguage(storage: Pick<Storage, 'getItem'> | undefined = safeLocalStorage()): Language | null {
  try {
    const v = storage?.getItem(LANGUAGE_STORAGE_KEY);
    return isLanguage(v) ? v : null;
  } catch {
    return null;
  }
}

export function storeLanguage(language: Language, storage: Pick<Storage, 'setItem'> | undefined = safeLocalStorage()): void {
  try {
    storage?.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // the choice still applies to this visit; it just won't be remembered
  }
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

/**
 * **WHICH LANGUAGE THE CLUBS PORTAL SHOWS** (Evan, 28 Sep): English while Spanish is closed; otherwise the choice this
 * browser remembers, then the signed-in staff member's own, then the browser's first language (Spanish if it is any
 * Spanish), then English.
 */
export function chooseLanguage(input: {
  open: boolean;
  stored: Language | null;
  account?: Language;
  browser: readonly string[];
}): Language {
  if (!input.open) return 'en';
  if (input.stored) return input.stored;
  if (input.account) return input.account;
  return input.browser[0]?.toLowerCase().startsWith('es') ? 'es' : 'en';
}

/**
 * **THE CLUBS PORTAL IN THE CHOSEN LANGUAGE** — wraps the clubs routes, never the admin's. It reads public config (no
 * session) to learn whether Spanish is open, applies the choice before the routes draw, redraws them on a change, and
 * draws the "English · Español" switch at the bottom of every page while Spanish is open. A choice made before sign-in
 * is saved to the staff member's own account once they are signed in.
 */
export function PortalLanguage({ children }: { children: ReactNode }) {
  const { data } = useClients();
  const session = useSession().session;
  const config = useQuery({ queryKey: ['public', 'config'], queryFn: () => data.publicConfig(), staleTime: 5 * 60_000 });
  const open = config.data?.languages?.includes('es') === true;
  const [stored, setStored] = useState<Language | null>(() => readStoredLanguage());
  const account = session?.user.language;
  const language = chooseLanguage({ open, stored, account, browser: browserLanguages() });
  // before the routes draw, so the first paint is already in the right language
  if (language !== portalLanguage()) setPortalLanguage(language);

  // a choice this browser made before signing in becomes the staff member's own, once
  const saved = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !stored || !session?.user || account === stored) return;
    const mark = `${session.user.sub}:${stored}`;
    if (saved.current === mark) return;
    saved.current = mark;
    data.updateLanguage(stored).catch(() => {
      saved.current = null; // try again next time; the browser still remembers it
    });
  }, [open, stored, session?.user, account, data]);

  const pick = (next: Language) => {
    storeLanguage(next);
    setStored(next);
  };

  return (
    <Fragment key={language}>
      {children}
      {open ? <LanguageSwitch current={language} onPick={pick} /> : null}
    </Fragment>
  );
}

/** "English · Español": the one not showing is a button; the one showing is plain text, marked current. */
export function LanguageSwitch({ current, onPick }: { current: Language; onPick: (language: Language) => void }) {
  const option = (language: Language, name: string) =>
    language === current ? (
      <span aria-current="true" lang={language}>
        {name}
      </span>
    ) : (
      <button type="button" className="language-switch-option" lang={language} onClick={() => onPick(language)}>
        {name}
      </button>
    );
  return (
    <nav className="language-switch" aria-label={deck.language.label} data-testid="language-switch">
      {option('en', deck.language.english)}
      <span aria-hidden="true"> · </span>
      {option('es', deck.language.spanish)}
    </nav>
  );
}
